import { LinkList, LinkListItem } from "components/link-list";
import styles from "./index.module.scss";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsTablet } from "hooks/useIsTablet";
import { useMemo, useCallback } from "react";
import FeaturedTrailerIcon from "assets/icons/display.svg?react";
import CustomizationIcon from "assets/icons/brandkit.svg?react";
import GatedIcon from "assets/icons/gated.svg?react";
import LinksIcon from "assets/icons/platforms.svg?react";
import Eye from "assets/icons/eye.svg?react";
import Desktop from "assets/icons/desktop.svg?react";
import Tablet from "assets/icons/tablet.svg?react";
import Mobile from "assets/icons/mobile.svg?react";
import { PlayerDetailsResponse } from "@switcherstudio/switcher-api-client";

export const CollectionLinkList: React.FC<{
    videoPlayer: PlayerDetailsResponse;
    hasVideoPlayerAccess: boolean;
    t: (key: string) => string;
    handlePreview: (type: "desktop" | "tablet" | "mobile") => void;
}> = ({ videoPlayer, hasVideoPlayerAccess, t, handlePreview }) => {
    const { isMobile } = useIsMobile();
    const { isTablet } = useIsTablet();

    const isDesktop = useMemo(() => {
        return !isMobile && !isTablet;
    }, [isMobile, isTablet]);

    const onPreviewClick = useCallback(
        (e: React.MouseEvent, type: "desktop" | "tablet" | "mobile") => {
            e.preventDefault();
            handlePreview(type);
        },
        [handlePreview]
    );

    const LinkListAction = useMemo(() => {
        let actions: LinkListItem[] = [];

        const previewLink = {
            Title: t("collections-page:preview"),
            Description: isMobile
                ? null
                : t("collections-page:preview-description"),
            Icon: <Eye />,
            NavigateTo: null,
            OnClick: isMobile ? (e) => onPreviewClick(e, "mobile") : null,
            Child: !isMobile && (
                <div className={styles["segmented-button"]}>
                    {isDesktop && (
                        <button
                            className={styles["segmented-button-option"]}
                            onClick={(e) => onPreviewClick(e, "desktop")}
                        >
                            <Desktop />
                            {t("catalog-page:desktop")}
                        </button>
                    )}
                    {(isTablet || isDesktop) && (
                        <button
                            className={styles["segmented-button-option"]}
                            onClick={(e) => onPreviewClick(e, "tablet")}
                        >
                            <Tablet />
                            {t("catalog-page:tablet")}
                        </button>
                    )}
                    <button
                        className={styles["segmented-button-option"]}
                        onClick={(e) => onPreviewClick(e, "mobile")}
                    >
                        <Mobile />
                        {t("catalog-page:mobile")}
                    </button>
                </div>
            )
        };
        actions.push(previewLink);

        if (hasVideoPlayerAccess) {
            actions.push({
                Title: t("collection-page:featured-trailer"),
                Icon: <FeaturedTrailerIcon />,
                NavigateTo: `/catalog/collections/${videoPlayer?.Id}/featured-trailer`
            });
        }

        if (hasVideoPlayerAccess) {
            actions.push({
                Title: t("shared:gating-options"),
                Icon: <GatedIcon />,
                NavigateTo: `/catalog/collections/${videoPlayer?.Id}/gated-content`
            });
        }

        actions.push({
            Title: t("collection-page:links"),
            Icon: <LinksIcon />,
            NavigateTo: `/catalog/collections/${videoPlayer?.Id}/links`
        });

        actions.push({
            Title: t("collection-page:customization"),
            Icon: <CustomizationIcon />,
            NavigateTo: `/catalog/collections/${videoPlayer?.Id}/customization`
        });

        return actions;
    }, [
        hasVideoPlayerAccess,
        t,
        onPreviewClick,
        isMobile,
        isTablet,
        isDesktop,
        videoPlayer
    ]);

    return (
        <>
            <LinkList actions={LinkListAction} />
        </>
    );
};
